import { recordNavigation } from '@/composables/useConsoleLogBuffer'
import VerifyEmail from '@/pages/VerifyEmail.vue'
import * as moduleRoutes from '@/router/routesLoader'
import { useCompaniesStore } from '@/stores/CompaniesSearch'
import { useAuthStore } from '@/stores/auth'
import { useCompanyStore } from '@/stores/company'
import { useCompanyBlockStore } from '@/stores/companyBlock'
import { useGeneralStore } from '@/stores/general'
import { storeToRefs } from 'pinia'
import { RouteRecordRaw, createRouter, createWebHistory } from 'vue-router'

import accountRoutes from '@/modules/account/routes/account.routes'
import authRoutes from '@/modules/auth/routes/auth.routes'
import InviteUserPage from '@/modules/users/pages/InviteUserPage.vue'

import { setupAnalytics } from './analytics'

const routes: RouteRecordRaw[] = [
	authRoutes,
	accountRoutes,
	{
		path: '/welcome',
		name: 'welcome',
		component: () => import('@/pages/WelcomePage.vue'),
		meta: {
			authRequired: false,
		},
	},
	{
		path: '/legal/privacy',
		name: 'PrivacyPolicy',
		component: () => import('@/pages/legal/PrivacyNoticePage.vue'),
		meta: {
			requiresAuth: false,
		},
	},
	{
		path: '/legal/terms-of-service',
		name: 'TermsOfService',
		component: () => import('@/pages/legal/TermsOfServicePage.vue'),
		meta: {
			requiresAuth: false,
		},
	},
	{
		path: '',
		meta: {
			authRequired: true,
		},
		children: [
			...(Object.values(moduleRoutes) as RouteRecordRaw[]),
			{
				path: '/dashboard',
				name: 'dashboard',
				meta: {
					authRequired: true,
				},
				component: () => import('@/pages/DashboardPage.vue'),
			},
			{
				path: 'debt/:id',
				name: 'debt',
				component: () => import('@/pages/DebtPage.vue'),
			},
			{
				path: '/contact',
				name: 'contact',
				meta: {
					authRequired: true,
				},
				component: () => import('@/pages/ContactUs.vue'),
				beforeEnter: (to, from, next) => {
					const authStore = useAuthStore()

					if (authStore.user?.internal) {
						next('/dashboard')
					} else {
						next()
					}
				},
			},
		],
		component: () => import('@/layouts/AuthenticatedLayout.vue'),
	},

	{
		path: '/error/:errorType',
		name: 'error',
		component: () => import('@/layouts/ErrorPageLayout.vue'),
		meta: {
			authRequired: false,
		},
	},
	{
		// Empresa suspendida/bloqueada por el backoffice. Sin authRequired: el
		// usuario cliente sigue autenticado (la sesión existe), pero toda ruta
		// protegida devuelve 423; aquí ve el motivo, un conteo de 15s y luego
		// logout automático.
		path: '/suspended',
		name: 'company-suspended',
		component: () => import('@/pages/CompanySuspended.vue'),
		meta: {
			authRequired: false,
		},
	},
	{
		path: '/users-register',
		component: InviteUserPage,
		beforeEnter: (to, from, next) => {
			if (to.query.company && to.query.expires && to.query.signature) {
				return next()
			} else {
				return next({
					name: 'welcome',
				})
			}
		},
	},
	{
		path: '/verify-email',
		component: VerifyEmail,
		beforeEnter: (to, from, next) => {
			const authStore = useAuthStore()

			if (authStore.user) {
				next('/dashboard')
			} else {
				next()
			}
		},
	},
	{
		path: '/:pathMatch(.*)*',
		name: 'not-found',
		component: () => import('@/layouts/ErrorPageLayout.vue'),
	},
]

const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	routes,
	scrollBehavior() {
		return { top: 0 }
	},
})

setupAnalytics(router)

/**
 * Hay una navegación en curso entre `beforeEach` y que el guard resuelva
 * (`next()`/afterEach/onError). El interceptor 423 de `@/lib/axios` la consulta
 * antes de hacer su propio `router.replace` a `company-suspended`: el `refetchAuth()`
 * de abajo es una petición real, y si esa misma petición devuelve 423, el
 * interceptor y el guard competían por resolver la navegación (ver su comentario) y
 * el resultado era un loop de replaces que nunca la dejaba asentar.
 */
export const navigationState = { pending: false }

router.beforeEach(async (to, from, next) => {
	navigationState.pending = true

	const { isAuthenticated, refetch: refetchAuth } = useAuthStore()
	const { setSidebarOpen } = useGeneralStore()
	const { setCompany, setId } = useCompanyStore()

	//Reset store before route change to de to or from
	if (to || from) {
		setCompany(undefined)
		setId(undefined)
	}

	setSidebarOpen(false)
	await refetchAuth()

	// Empresa apagada (el 423 de /api/user/refetchAuth marcó el store): la
	// pantalla de suspensión gana sobre cualquier otra regla (incluida la de
	// authRequired→login), para que el cliente vea el motivo y su conteo.
	const { blocked: companyBlocked } = useCompanyBlockStore()
	if (companyBlocked) {
		return to.name === 'company-suspended' ?
				next()
			:	next({ name: 'company-suspended' })
	}

	if (to.fullPath === '/') {
		next({ name: isAuthenticated().value ? 'dashboard' : 'welcome' })
	} else if (
		to.matched.some((record) => record.meta.onlyLoggedOut) &&
		isAuthenticated().value
	) {
		next({ path: '/' })
	} else if (
		to.matched.some((record) => record.meta.authRequired) &&
		!isAuthenticated().value
	) {
		next({ name: 'login' })
	} else {
		next()
	}
})

// Rastro de navegación para el `error_log` de los reportes de bug: sin él solo se
// sabe en qué pantalla se reportó, no cómo llegó ahí.
//
// `path` y no `fullPath`: el query de las rutas de hotel trae `users` y
// `previousRequestData` serializados, o sea PII, y este log termina en el bucket
// que lee soporte. De paso cada entrada baja de ~1.4 KB a unas decenas de bytes:
// MAX_ENTRIES cuenta entradas, no bytes, así que las URLs gigantes desalojaban los
// errores que sí importan.
router.afterEach((to, from) => {
	navigationState.pending = false
	recordNavigation(from.path, to.path)
})

router.onError(() => {
	navigationState.pending = false
})

export default router
